import { gql, NetworkStatus, useQuery } from '@apollo/client'

import { currentArtistVar, useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  ArtistPostsFragmentDoc,
  ArtistPostVideosQuery,
  ArtistPostVideosQueryVariables,
  CurrentUserFragmentDoc,
  FetchNewArtistPostVideosQuery,
  FetchNewArtistPostVideosQueryVariables,
  PostBoxFragmentDoc,
  PostType,
  StoryFilter,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'
/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ artistId: string }>()
  const artistId = router.query.artistId ?? config.artistId

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewArtistPostVideosQuery,
    FetchNewArtistPostVideosQueryVariables
  >({
    query: FETCH_NEW_ARTIST_POST_VIDEOS,
    variables: {
      artistId,
      resourceTypes: [PostType.Video],
      storyFilter: StoryFilter.Include,
      pageInput: { limit: 12 }, // NOTE: utoniqPaginationのmerge処理に必要なため削除NG
      pageInputById: { limit: 12 },
      categoryId: null,
    },
    identify: { __typename: 'Artist', id: artistId },
    fields: [
      {
        fieldName: 'posts',
        responsePath: 'artist.posts',
        fragment: PostBoxFragmentDoc,
      },
    ],
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })
  const { data, fetchMore, networkStatus } = useQuery<
    ArtistPostVideosQuery,
    ArtistPostVideosQueryVariables
  >(ARTIST_POST_VIDEOS, {
    variables: {
      artistId,
      resourceTypes: [PostType.Video],
      storyFilter: StoryFilter.Include,
      pageInput: { limit: 12 }, // NOTE: utoniqPaginationのmerge処理に必要なため削除NG
      pageInputById: { limit: 12 },
      categoryId: null,
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      isFirstLoadRef.current = false
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })
  const isNextFetch = !!data?.artist?.posts
  const pageInfo = data?.artist?.posts?.pageInfo
  const fetchMorePosts = async () => {
    if (pageInfo && pageInfo.hasNextPage) {
      await fetchMore({
        variables: {
          artistId,
          // NOTE: utoniqPaginationのmerge処理に必要なため削除NG
          pageInput: {
            cursor: pageInfo.endCursor || null,
            limit: 6,
          },
          pageInputById: {
            cursor: pageInfo.endCursor || null,
            limit: 6,
          },
        },
      })
    }
  }

  const nodes =
    data?.artist?.posts.edges
      // NOTE YouTube動画は対象外とする
      .filter(
        ({ node }) =>
          node.resource?.uri !== null && node.resource?.videoId === null
      )
      .map(({ node }) => node) || []

  return {
    state: {
      user: data?.user,
      isMembershipPersonalInformationRequired:
        !!data?.user?.isMembershipPersonalInformationRequired,
      posts: nodes,
      loading: networkStatus === NetworkStatus.loading && !isNextFetch,
      refetching: networkStatus === NetworkStatus.fetchMore,
      artistName: data?.artist?.displayName || '',
      hasNextPage: !!pageInfo?.hasNextPage,
      liveStreamId: data?.artist?.currentLive?.id,
    },
    actions: {
      onFetchMore: fetchMorePosts,
    },
  }
})

const ARTIST_POST_VIDEOS = gql`
  query ArtistPostVideos(
    $artistId: ID!
    $pageInput: PageInputDate
    $pageInputById: PageInput
    $resourceTypes: [PostType!]
    $storyFilter: StoryFilter!
    $categoryId: String
  ) {
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      displayName
      posts(
        pageInput: $pageInput
        pageInputById: $pageInputById
        resourceTypes: $resourceTypes
        storyFilter: $storyFilter
        categoryId: $categoryId
      ) {
        ...ArtistPosts
      }
      currentLive {
        id
      }
    }
    user {
      ...CurrentUser
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${ArtistPostsFragmentDoc}
  ${CurrentUserFragmentDoc}
`

const FETCH_NEW_ARTIST_POST_VIDEOS = gql`
  query FetchNewArtistPostVideos(
    $artistId: ID!
    $pageInput: PageInputDate
    $pageInputById: PageInput
    $resourceTypes: [PostType!]
    $storyFilter: StoryFilter!
    $categoryId: String
  ) {
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      posts(
        pageInput: $pageInput
        pageInputById: $pageInputById
        resourceTypes: $resourceTypes
        storyFilter: $storyFilter
        categoryId: $categoryId
      ) {
        ...ArtistPosts
      }
      currentLive {
        id
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${ArtistPostsFragmentDoc}
`
